// Lib
import { createSelector } from 'reselect';
import * as Immutable from 'immutable';
import { uniq } from 'lodash/fp';

// Utils
import { getFirstSelectedBackgroundColor, getFirstSelectedColor, getSelectedElementType } from './colorToolUtils';
import { getElementColorPickerHexValue } from '../../../../../../common/colors/elementColorPickerUtils';
import { getColorCssValue } from '../../../../../element/utils/elementColorStyleUtils';
import { isCard, isColumn, isImage, isSketch, isTask } from '../../../../../../common/elements/utils/elementTypeUtils';
import { getElementType, getIsTransparentImage } from '../../../../../../common/elements/utils/elementPropertyUtils';
import { asObject } from '../../../../../../common/utils/immutableHelper';
import { getSelectedElements } from '../../../../../element/selection/selectedElementsSelector';
import { getIsFeatureEnabledForCurrentUser } from '../../../../../element/feature/elementFeatureSelector';

// Constants
import { BACKGROUND_COLORS, DEFAULT_ELEMENT_COLOR } from '../../../../../../common/colors/colorConstants';
import { getBackgroundPredefinedColorMapForType } from '../../../../../../common/colors/colorPresetsUtil';
import { ExperimentId } from '../../../../../../common/experiments/experimentsConstants';
import { ImMNElement, MNElement } from '../../../../../../common/elements/elementModelTypes';
import { getMilanoteApplicationModeSelector } from '../../../../../platform/platformSelector';
import { MilanoteApplicationMode } from '../../../../../../common/platform/platformTypes';
import { getThemeIsDarkMode } from '../../../../../theme/themeSelector';

export type ColorToolSelectorReturnType = {
    name: string;
    elementType: string;
    supportBackgroundColor: boolean;
    selectedColor: string | null;
    selectedColorHex: string;
    selectedBackgroundColorName: string;
    selectedBackgroundColor: string | null;
    selectedBackgroundColorHex: string;
};

/**
 * Images only opt into background-color editing in Image Modal V2 when the source image is transparent.
 */
const supportsImageBackgroundColor = (element: MNElement, isImageModalV2Enabled: boolean) =>
    isImageModalV2Enabled && isImage(element) && getIsTransparentImage(element);

export const colorToolSelector = createSelector(
    (state: Immutable.Map<string, object>, ownProps: { selectedElements?: Immutable.List<ImMNElement> }) =>
        // Tasks need to get the selected element from the state, to ensure it's the containing task list
        ownProps.selectedElements?.every(isTask) ? getSelectedElements(state) : ownProps.selectedElements,
    (state: Immutable.Map<string, object>, ownProps: { name: string }) => ownProps.name,
    getThemeIsDarkMode,
    (state: Immutable.Map<string, object>, ownProps) => getMilanoteApplicationModeSelector(state),
    getIsFeatureEnabledForCurrentUser(ExperimentId.imageModalV2),
    (selectedElements, name, isDarkMode, appMode, isImageModalV2Enabled): ColorToolSelectorReturnType => {
        const selectedColorName = getFirstSelectedColor(selectedElements);
        const selectedColor = getColorCssValue(selectedColorName);
        const selectedElementType = getSelectedElementType(selectedElements);
        const selectedColorHex =
            getElementColorPickerHexValue({
                color: selectedColorName,
                elementType: selectedElementType,
                isDarkMode,
            }) || DEFAULT_ELEMENT_COLOR;
        const backgroundPredefinedColorMap = getBackgroundPredefinedColorMapForType(selectedElementType);

        const firstSelectedElement = selectedElements?.first();
        const singleSelectedImageSupportsBackgroundColor =
            !!firstSelectedElement &&
            selectedElements.size === 1 &&
            supportsImageBackgroundColor(firstSelectedElement, isImageModalV2Enabled);
        const firstSelectedBackgroundColorName = getFirstSelectedBackgroundColor(selectedElements);
        const selectedBackgroundColorName =
            singleSelectedImageSupportsBackgroundColor && !firstSelectedBackgroundColorName
                ? BACKGROUND_COLORS.TRANSPARENT.name
                : firstSelectedBackgroundColorName;
        const selectedBackgroundColor = getColorCssValue(selectedBackgroundColorName, backgroundPredefinedColorMap);
        const selectedBackgroundColorHex =
            getElementColorPickerHexValue({
                color: selectedBackgroundColorName,
                elementType: selectedElementType,
                isDarkMode,
                isBackground: true,
            }) || DEFAULT_ELEMENT_COLOR;

        const selectedElementsSupportBackgroundColor = selectedElements.every(
            (el: MNElement) => isCard(el) || isSketch(el) || isColumn(el),
        );
        const supportBackgroundColor =
            selectedElementsSupportBackgroundColor || singleSelectedImageSupportsBackgroundColor;
        const selectedTypes = uniq(asObject(selectedElements.map(getElementType)));

        return {
            name: selectedTypes.length > 1 || appMode === MilanoteApplicationMode.mobile ? 'Color' : name,
            elementType: getElementType(selectedElements?.first()),
            supportBackgroundColor,
            selectedColor,
            selectedColorHex,
            selectedBackgroundColorName,
            selectedBackgroundColor,
            selectedBackgroundColorHex,
        };
    },
);
